import { formatROI } from '../../formatROI';
import {
  createQuoteWithROIandProjectedCoverage,
  createTraditionalLTCQuoteWithROIandProjectedCoverage,
} from './calcClientROIProjections';

type Quote = PolicyQuote & {
  projectedCostCoverage?: number;
  maxAchievableCoverage?: number;
  discountSavings?: number;
  discountPolicyType?: number;
  roi?: number;
  roiValues?: {
    projectedReturnOnInvestment: number;
    projectedCompoundAnnualGrowthRate: number;
    projectedInternalRateOfReturn: number | undefined;
    investmentPeriodYears: number;
  };
  formattedROI?: string;
  illustrationPdf?: Blob;
  isCreatingIllustration?: boolean;
  illustrationError?: string | null;
  updatedQuote?: Quote | null;
  illustrationPdfUrl?: string | null;
  responseData?: any;
};

interface EnrichQuotesOptions {
  includeOriginalROIValues?: boolean;
  handleTraditionalLTC?: boolean;
}

export interface EnrichedQuote extends Quote {
  roi?: number;
  roiValues?: {
    projectedReturnOnInvestment: number;
    projectedCompoundAnnualGrowthRate: number;
    projectedInternalRateOfReturn: number | undefined;
    investmentPeriodYears: number;
  };
  formattedROI?: string;
  maxAchievableCoverage?: number;
  originalROIValues?: {
    projectedReturnOnInvestment?: number;
    projectedCompoundAnnualGrowthRate?: number;
    projectedInternalRateOfReturn?: number;
    investmentPeriodYears?: number;
  };
}

function scaleValue(value: number | undefined, scalingFactor: number): number | undefined {
  return value !== undefined ? value * scalingFactor : undefined;
}

/**
 * Adjusts an EquiTrust quote's LTC benefits based on a new coverage ratio.
 * LTC benefits scale proportionally with coverage ratio changes.
 * Premium and death benefit remain unchanged.
 */
function adjustQuoteForCoverageRatio(originalQuote: Quote, newCoverageRatio: number): Quote {
  const originalCoverageRatio = originalQuote.coverageRatio;
  if (!originalCoverageRatio || originalCoverageRatio <= 0) {
    return originalQuote;
  }

  const minRatio = originalQuote.minCoverageRatio || originalCoverageRatio;
  const maxRatio = originalQuote.maxCoverageRatio || originalCoverageRatio;
  const clampedRatio = Math.max(minRatio, Math.min(maxRatio, newCoverageRatio));

  const scalingFactor = clampedRatio / originalCoverageRatio;

  return {
    ...originalQuote,
    coverageRatio: clampedRatio,
    ltcBenefitMonthlyIndividual: originalQuote.ltcBenefitMonthlyIndividual * scalingFactor,
    ltcBenefitAnnualIndividual: scaleValue(originalQuote.ltcBenefitAnnualIndividual, scalingFactor),
    ltcBenefitTotal: originalQuote.ltcBenefitTotal * scalingFactor,
    projectedCostCoverage: undefined,
    roi: undefined,
    roiValues: undefined,
    formattedROI: undefined,
  };
}

/**
 * Enriches quotes with ROI calculations and formatted ROI values.
 * Handles both regular and traditional LTC quotes with error handling.
 * Auto-adjusts EquiTrust quotes to max coverage ratio for best LTC benefits.
 *
 * @param quotes - Array of quotes to enrich
 * @param client - Client data for ROI calculations
 * @param calculationType - Type of ROI calculation to perform
 * @param options - Additional options for enrichment
 * @returns Array of enriched quotes (quotes with calculation errors are filtered out)
 */
export function enrichQuotesWithROI(
  quotes: Quote[],
  client: Client,
  calculationType: ReturnOnInvestmentCalculationType,
  options: EnrichQuotesOptions = {},
): EnrichedQuote[] {
  const { includeOriginalROIValues = false, handleTraditionalLTC = false } = options;

  return quotes
    .map<EnrichedQuote | null>(quote => {
      try {
        // Check if this is a traditional LTC quote (only if enabled)
        const isTraditionalLTC =
          handleTraditionalLTC && !!(quote as unknown as TraditionalLTCQuote).traditionalLTCQuoteId;

        // Auto-adjust EquiTrust quotes to max coverage ratio for best LTC benefits
        const coverageAdjusted =
          !isTraditionalLTC && quote.maxCoverageRatio && quote.coverageRatio
            ? adjustQuoteForCoverageRatio(quote, quote.maxCoverageRatio)
            : quote;

        let enriched: {
          roi: number;
          roiValues: {
            projectedReturnOnInvestment: number;
            projectedCompoundAnnualGrowthRate: number;
            projectedInternalRateOfReturn: number | undefined;
            investmentPeriodYears: number;
          };
          projectedCostCoverage: number;
          discountSavings: number;
          discountPolicyType: number | undefined;
          [key: string]: unknown;
        };

        if (isTraditionalLTC) {
          enriched = createTraditionalLTCQuoteWithROIandProjectedCoverage(
            coverageAdjusted as unknown as TraditionalLTCQuote,
            client,
            calculationType,
          );
        } else {
          enriched = createQuoteWithROIandProjectedCoverage(
            coverageAdjusted,
            client,
            calculationType,
          );
        }

        const formattedROI = formatROI(enriched.roiValues, calculationType);

        const result = {
          ...enriched,
          formattedROI,
        } as EnrichedQuote;

        // Optionally include original ROI values for preservation
        if (includeOriginalROIValues && enriched.roiValues) {
          result.originalROIValues = {
            projectedReturnOnInvestment: enriched.roiValues.projectedReturnOnInvestment,
            projectedCompoundAnnualGrowthRate: enriched.roiValues.projectedCompoundAnnualGrowthRate,
            projectedInternalRateOfReturn: enriched.roiValues.projectedInternalRateOfReturn,
            investmentPeriodYears: enriched.roiValues.investmentPeriodYears,
          };
        }

        return result;
      } catch (error) {
        const quoteId =
          (quote as Quote).insuranceQuoteId ||
          (quote as unknown as TraditionalLTCQuote).traditionalLTCQuoteId;
        console.error('Error calculating ROI for quote:', quoteId, error);
        return null;
      }
    })
    .filter((q): q is EnrichedQuote => q !== null);
}
